import { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
// N9-v2: preload target chunk on hover/focus. Drop-in for <Link>.
import { PreloadLink as Link } from "@/components/PreloadLink";
import { SITE_URL } from "@/lib/constants";
import { OG_IMAGE_DEFAULT } from "@/lib/r2-assets";
import Breadcrumb from "@/components/layout/Breadcrumb";
import { products, productFamilies, productGroups, productLines, ProductSpecs } from "@/lib/data";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Package, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import FadeImage from "@/components/ui/FadeImage";
import { smallSlotSrc, smallSlotSources } from "@/lib/image-sources";
import CTASection from "@/components/home/CTASection";

const fixadoresSpecKeys: { key: keyof ProductSpecs; label: string }[] = [
  { key: "material", label: "Material" },
  { key: "diameter", label: "Diâmetro" },
  { key: "length", label: "Comprimento" },
  { key: "headType", label: "Tipo de Cabeça" },
  { key: "bodyFormat", label: "Formato do Corpo" },
  { key: "rivetableThickness", label: "Espessura Rebitável" },
  { key: "tensileStrength", label: "Resistência à Tração" },
  { key: "shearStrength", label: "Resistência ao Cisalhamento" },
  { key: "weight", label: "Peso" },
  { key: "finish", label: "Acabamento" },
];

const ferramentasSpecKeys: { key: keyof ProductSpecs; label: string }[] = [
  { key: "tractionForce", label: "Tração" },
  { key: "operatingPressure", label: "Pressão de Exercício" },
  { key: "minAirTubeDiameter", label: "Diâm. Mín. Tubo de Ar" },
  { key: "airConsumptionPerCycle", label: "Consumo de Ar por Ciclo" },
  { key: "maxForce", label: "Força Máxima" },
  { key: "stroke", label: "Curso" },
  { key: "capacity", label: "Capacidade" },
  { key: "weight", label: "Peso" },
  { key: "operatingTemperature", label: "Temperatura de Utilização" },
  { key: "vibration", label: "Vibração (Ac)" },
  { key: "soundPressureA", label: "Pressão Acústica (A)" },
  { key: "soundPressureC", label: "Pressão Acústica (C)" },
  { key: "soundPowerA", label: "Potência Acústica (A)" },
];

function getProductLineId(product: (typeof products)[0]): string {
  const family = productFamilies.find((f) => f.id === product.familyId);
  return family?.lineId ?? "";
}

function getProductUrl(product: (typeof products)[0]) {
  const group = productGroups.find((g) => g.id === product.groupId);
  const family = productFamilies.find((f) => f.id === product.familyId);
  const line = productLines.find((l) => l.id === family?.lineId);
  return `/produtos/${line?.slug}/${family?.slug}/${group?.slug}/${product.id}`;
}

function getProductLabel(product: (typeof products)[0]) {
  const group = productGroups.find((g) => g.id === product.groupId);
  return `${group?.name ? `${group.name} — ` : ""}${product.name}`;
}

const MAX_SLOTS = 3;

const Compare = () => {
  const [searchParams] = useSearchParams();
  const initialId = searchParams.get("p");
  const linhaParam = searchParams.get("linha");

  // Determine initial line from URL param product or linha param
  const initialLine = useMemo(() => {
    if (linhaParam === "ferramentas" || linhaParam === "fixadores") return linhaParam;
    if (!initialId) return "fixadores";
    const p = products.find((pr) => pr.id === initialId);
    if (!p) return "fixadores";
    return getProductLineId(p) || "fixadores";
  }, [initialId, linhaParam]);

  const [activeLine, setActiveLine] = useState<string>(initialLine);

  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    initialId && products.find((p) => p.id === initialId) ? [initialId] : []
  );

  const selected = useMemo(
    () => selectedIds.map((id) => products.find((p) => p.id === id) || null),
    [selectedIds]
  );

  // Filter products available in dropdown based on active line
  const availableProducts = useMemo(() => {
    return products.filter((p) => getProductLineId(p) === activeLine);
  }, [activeLine]);

  // When switching line, clear selections
  const handleLineChange = (line: string) => {
    if (line !== activeLine) {
      setActiveLine(line);
      setSelectedIds([]);
    }
  };

  // Choose spec keys based on active line
  const specKeys = activeLine === "ferramentas" ? ferramentasSpecKeys : fixadoresSpecKeys;

  const updateSlot = (index: number, id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id) && prev[index] !== id) return prev;
      const next = [...prev];
      next[index] = id;
      return next;
    });
  };

  const addProductToNextSlot = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.length >= MAX_SLOTS || prev.includes(id)) return prev;
      return [...prev, id];
    });
  };

  const [clearKey, setClearKey] = useState(0);
  const clearAll = () => {
    setSelectedIds([]);
    setClearKey((k) => k + 1);
  };

  const [descExpanded, setDescExpanded] = useState(false);
  const filledCount = selected.filter(Boolean).length;
  const gridCols = "180px repeat(3, minmax(180px, 1fr))";

  return (
    <>
      <Helmet>
        <title>Comparador de Rebites e Fixadores | Or Brasil</title>
        <meta name="description" content="Compare rebites cegos, estruturais, porcas rebite e demais fixadores lado a lado: material, diâmetro, espessura e força de tração." />
        <link rel="canonical" href={`${SITE_URL}/comparador`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Comparador de Rebites e Fixadores | Or Brasil" />
        <meta property="og:description" content="Compare rebites cegos, estruturais, porcas rebite e demais fixadores lado a lado: material, diâmetro, espessura e força de tração." />
        <meta property="og:url" content={`${SITE_URL}/comparador`} />
        <meta property="og:site_name" content="Or Brasil" />
        <meta property="og:image" content={OG_IMAGE_DEFAULT} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Comparador de Rebites e Fixadores | Or Brasil" />
        <meta name="twitter:description" content="Compare rebites cegos, estruturais, porcas rebite e demais fixadores lado a lado: material, diâmetro, espessura e força de tração." />
        <meta name="twitter:image" content={OG_IMAGE_DEFAULT} />
      </Helmet>

      <section className="relative min-h-[280px] flex items-center bg-surface-dark">
        <div className="absolute inset-0 opacity-[0.02] bg-grid-fade" />
        <div className="absolute top-1/2 right-0 -translate-y-1/2 w-[600px] h-[600px] bg-primary/8 rounded-full blur-[140px]" aria-hidden="true" />
        <div className="w-full container-industrial relative z-10 py-16">
          <Breadcrumb items={[{ label: "Comparador" }]} />
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-3">Comparador</h1>
          <p className="text-lg text-on-surface-dark-muted max-w-lg">
            Selecione até 3 modelos e compare suas especificações lado a lado.
          </p>
        </div>
      </section>

      <section className="container-industrial py-10 md:py-14">
        {/* Line selector */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center gap-1 p-1.5 rounded-full bg-primary/5 border border-primary/20">
            <button
              onClick={() => handleLineChange("fixadores")}
              className={`inline-flex items-center justify-center min-w-[140px] px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                activeLine === "fixadores"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Fixadores
            </button>
            <button
              onClick={() => handleLineChange("ferramentas")}
              className={`inline-flex items-center justify-center min-w-[140px] px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${
                activeLine === "ferramentas"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              Ferramentas
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <div className="min-w-[760px]">
            {/* Product slots — always show the 3-column grid */}
            <div style={{ display: "grid", gridTemplateColumns: gridCols }}>
              <div className="px-4 py-2">
                {filledCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAll}
                    className="text-muted-foreground gap-1.5 text-xs h-8 -ml-2"
                  >
                    <Trash2 className="w-3 h-3" />
                    Limpar
                  </Button>
                )}
              </div>

                {Array.from({ length: MAX_SLOTS }).map((_, index) => {
                  const id = selectedIds[index];
                  const product = id ? products.find((p) => p.id === id) : undefined;
                  const isActive = index < selectedIds.length;
                  const isAddSlot = !isActive && index <= selectedIds.length;

                  return (
                    <div key={`${index}-${clearKey}`} className="px-3 py-2">
                      {isActive ? (
                        <>
                          <Select value={id ?? ""} onValueChange={(val) => updateSlot(index, val)}>
                            <SelectTrigger className="h-11 rounded-full border border-primary/30 bg-primary/5 hover:bg-primary/10 hover:border-primary/50 text-foreground text-sm font-semibold w-full transition-colors shadow-sm cursor-pointer touch-manipulation">
                              <SelectValue placeholder="Selecione..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableProducts.map((p) => (
                                <SelectItem
                                  key={p.id}
                                  value={p.id}
                                  disabled={selectedIds.includes(p.id) && p.id !== id}
                                >
                                  {getProductLabel(p)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {product ? (
                            <Link to={getProductUrl(product)} className="flex justify-center mt-6 group">
                              {/* Comparador product tile — show the
                                  actual photo when the CMS has one;
                                  fall back to the Package icon only
                                  when there's no image at all.
                                  Previously every slot rendered the
                                  icon regardless of available
                                  artwork (item 19). */}
                              {/* WHITE behind the photo (was bg-muted/50,
                                  greying on hover): the image is
                                  object-contain in a square slot, so a
                                  non-square product letterboxes and the
                                  backing colour shows. Same rule as the
                                  tiles and the nav thumbnails. */}
                              <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl bg-white overflow-hidden flex items-center justify-center transition-colors">
                                {product.image ? (
                                  <FadeImage
                                    src={smallSlotSrc(product.imageSources, product.image)}
                                    sources={smallSlotSources(product.imageSources)}
                                    alt={product.name}
                                    // Fit, don't fill — this is a product
                                    // photo in a square slot, so cover was
                                    // cropping the tool in the comparator.
                                    objectFit="contain"
                                    className="w-full h-full group-hover:scale-110 transition-transform duration-700"
                                    loading="lazy"
                                  />
                                ) : (
                                  <Package className="w-12 h-12 text-muted-foreground/20" />
                                )}
                              </div>
                            </Link>
                          ) : (
                            <div className="flex justify-center mt-6">
                              <div className="w-28 h-28 md:w-36 md:h-36 rounded-2xl bg-muted/50 flex items-center justify-center">
                                <Package className="w-12 h-12 text-muted-foreground/20" />
                              </div>
                            </div>
                          )}

                          <div className="text-center mt-3 mb-2 min-h-[48px]">
                            {product && (
                              <>
                                <Link
                                  to={getProductUrl(product)}
                                  className="text-sm md:text-base font-bold text-foreground hover:text-primary transition-colors line-clamp-1"
                                >
                                  {product.name}
                                </Link>
                                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                  {product.shortDescription}
                                </p>
                              </>
                            )}
                          </div>
                        </>
                      ) : isAddSlot ? (
                        <>
                          <Select onValueChange={(id) => {
                            if (selectedIds.length === 0) {
                              setSelectedIds([id]);
                            } else {
                              addProductToNextSlot(id);
                            }
                          }}>
                            <SelectTrigger className="relative h-11 rounded-full border-2 border-dashed border-border/50 hover:border-primary/40 hover:bg-primary/5 text-muted-foreground text-sm font-medium w-full transition-colors cursor-pointer touch-manipulation pl-11">
                              <Plus className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                              <SelectValue placeholder="Adicionar" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableProducts.map((p) => (
                                <SelectItem key={p.id} value={p.id} disabled={selectedIds.includes(p.id)}>
                                  {getProductLabel(p)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </div>

            {/* Specs table aligned with same columns */}
            {filledCount > 0 && (
              <div className="bg-muted/40 border-t border-b border-border/40 py-6 mt-2">
                <div style={{ display: "grid", gridTemplateColumns: gridCols }} className="border-b border-border/30">
                  <div className="py-3.5 px-4 font-medium text-foreground text-sm flex items-center gap-1.5">
                    Descrição
                    <button
                      onClick={() => setDescExpanded((v) => !v)}
                      className="text-muted-foreground hover:text-primary transition-colors"
                      aria-label={descExpanded ? "Recolher descrições" : "Expandir descrições"}
                    >
                      {descExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                  {Array.from({ length: MAX_SLOTS }).map((_, index) => {
                    const product = selected[index];
                    const text = product
                      ? product.longDescription || product.description
                      : "";
                    return (
                      <div key={index} className="py-3.5 px-3 text-muted-foreground text-sm leading-relaxed break-words">
                        {text ? (descExpanded ? text : `${text.slice(0, 180)}${text.length > 180 ? "…" : ""}`) : ""}
                      </div>
                    );
                  })}
                </div>

                {specKeys.map(({ key, label }, rowIndex) => {
                  const values = Array.from({ length: MAX_SLOTS }).map((_, index) => selected[index]?.specs[key] || "");
                  const nonEmptyValues = values.filter(Boolean);
                  const allSame = nonEmptyValues.length <= 1 || nonEmptyValues.every((v) => v === nonEmptyValues[0]);

                  return (
                    <div
                      key={key}
                      style={{ display: "grid", gridTemplateColumns: gridCols }}
                      className={`border-b border-border/30 last:border-0 ${rowIndex % 2 !== 0 ? "bg-muted/20" : ""}`}
                    >
                      <div className="py-3.5 px-4 font-medium text-foreground text-sm">{label}</div>
                      {values.map((value, index) => (
                        <div
                          key={index}
                          className={`py-3.5 px-3 text-sm break-words ${!allSame && value ? "text-primary font-semibold" : "text-muted-foreground"}`}
                        >
                          {value || ""}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

      </section>

      <CTASection />
    </>
  );
};

export default Compare;
